import type { ContrarianAlert } from "./contrarianEngine";
import type { DerivedSnapshot } from "@/lib/derivedIntel/derivedIntelligenceEngine";

/**
 * Contrarian Orchestrator — turns the raw list of contrarian alerts into
 * a priority-scored, debounced stream. The goal is to surface AT MOST one
 * major contrarian event per relevant emotional window so we never spam
 * contradictions.
 *
 * Pure scoring — no I/O, no realtime polling.
 */

export interface ContrarianPriority {
  alert: ContrarianAlert;
  /** Final composite score 0..1 used for ranking. */
  score: number;
  /** Breakdown for debugging / war room. */
  breakdown: {
    emotional: number;
    viral: number;
    bettingRelevance: number;
    narrativeSurprise: number;
    confidenceConflict: number;
  };
}

const EMOTIONAL_WEIGHT_BY_KIND: Record<ContrarianAlert["kind"], number> = {
  stats_vs_momentum: 0.95,
  resisting_pressure: 0.9,
  underdog_surviving: 0.85,
  false_dominance: 0.65,
  possession_without_danger: 0.55,
};

const BETTING_WEIGHT_BY_KIND: Record<ContrarianAlert["kind"], number> = {
  stats_vs_momentum: 0.85,
  resisting_pressure: 0.7,
  underdog_surviving: 0.75,
  false_dominance: 0.6,
  possession_without_danger: 0.4,
};

/** Minimum gate — drop alerts below this composite score. */
export const CONTRARIAN_MIN_SCORE = 0.55;

export function scoreContrarian(
  alert: ContrarianAlert,
  snap: DerivedSnapshot,
  confidenceScore: number,
): ContrarianPriority {
  const s = snap.signals;
  const emotional =
    (EMOTIONAL_WEIGHT_BY_KIND[alert.kind] ?? 0.5) *
    (0.55 + 0.45 * s.narrativeIntensity);
  const viral = alert.shareability;
  const bettingRelevance =
    (BETTING_WEIGHT_BY_KIND[alert.kind] ?? 0.5) *
    (0.6 + 0.4 * Math.max(s.goalProbabilityHome, s.goalProbabilityAway));
  const narrativeSurprise = clamp01(
    Math.abs(s.momentumShift) * 0.8 + (s.pressureExtreme ? 0.2 : 0),
  );
  // Confidence conflict: contradictions matter MORE when our base read is
  // confident — surprising the confident eye is the viral hook.
  const confidenceConflict = clamp01(0.4 + confidenceScore * 0.6);

  const score = clamp01(
    emotional * 0.3 +
      viral * 0.25 +
      bettingRelevance * 0.15 +
      narrativeSurprise * 0.2 +
      confidenceConflict * 0.1,
  );

  return {
    alert,
    score,
    breakdown: { emotional, viral, bettingRelevance, narrativeSurprise, confidenceConflict },
  };
}

/**
 * Returns at most ONE dominant contrarian alert per emotional window. If
 * no alert clears the minimum score, returns null. Caller decides cadence.
 */
export function pickDominantContrarian(
  alerts: ContrarianAlert[],
  snap: DerivedSnapshot,
  confidenceScore: number,
): ContrarianPriority | null {
  if (alerts.length === 0) return null;
  const ranked = alerts
    .map((a) => scoreContrarian(a, snap, confidenceScore))
    .sort((a, b) => b.score - a.score);
  const top = ranked[0];
  if (!top || top.score < CONTRARIAN_MIN_SCORE) return null;
  return top;
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}